import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/solid';
import Logo from '../components/Logo';

const LandingPage: React.FC = () => {
  const trianglePattern = [
    [0, 0, 0, 1],
    [0, 0, 1, 1],
    [0, 1, 1, 1],
    [1, 1, 1, 1],
  ];

  const barHeights = [2, 3, 5, 4, 6];
  const streamSquares = Array.from({ length: 10 });

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50 text-gray-900">
      {/* Header */}
      <header className="relative z-10 px-6 py-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <Logo size="md" />
          
          {/* Navigation */}
          <div className="flex items-center space-x-8">
            <nav className="hidden md:flex items-center space-x-8 text-sm font-medium text-gray-700">
              <a href="#features" className="hover:text-gray-900">
                Features
              </a>
              <a href="#pricing" className="hover:text-gray-900">
                Pricing
              </a>
              <a href="#resources" className="hover:text-gray-900">
                Resources
              </a>
            </nav>
            <Link
              to="/login"
              className="inline-flex items-center justify-center rounded-lg bg-black px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-900"
            >
              Login
            </Link>
          </div>
        </div>
      </header>

      {/* Hero & Metrics Sections */}
      <main className="relative px-6 pb-24 pt-10 md:pt-16">
        {/* Hero Section */}
        <section id="hero" className="mx-auto flex max-w-5xl flex-col items-center text-center">
          <h1 className="mb-5 flex flex-wrap items-center justify-center gap-2 text-3xl font-semibold tracking-tight text-gray-900 md:text-4xl">
            <span>Say&nbsp;hello&nbsp;to</span>
            <span className="inline-flex items-center gap-2">
              <span className="inline-block -rotate-6">
                <img
                  src="/icon.png"
                  alt="Certs icon"
                  className="block h-9 w-9 rounded-xl object-contain"
                />
              </span>
              <span>Certs&nbsp;App</span>
            </span>
          </h1>

          <p className="mb-6 max-w-2xl text-base text-gray-700 md:text-lg">
            Create quotes, manage jobs, and send certificates instantly with a clean, modern platform
            built for trades.
          </p>

          <div className="mb-12">
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 rounded-lg bg-orange-500 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-600"
            >
              <span>Get Early Access</span>
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-4 w-full max-w-5xl mx-auto">
            <img
              src="/macbook-mockup.png"
              alt="Certs app on MacBook"
              className="hero-macbook-image w-full max-w-5xl object-contain"
            />
          </div>
        </section>

        {/* Metrics / Features Section */}
        <section id="features" className="mx-auto mt-24 max-w-6xl">
          <h2 className="text-center text-2xl font-semibold text-gray-900 md:text-3xl">
            The UK's smartest trades workflow.
          </h2>

          <div className="mt-12 grid gap-6 md:grid-cols-[minmax(0,1.05fr),minmax(0,0.95fr)]">
            {/* Left column: two stacked metric cards */}
            <div className="grid gap-6">
              <div className="metric-card flex flex-col justify-between rounded-3xl bg-white p-8 shadow-sm">
                <div className="flex items-center justify-between gap-6">
                  <div>
                    <div className="text-4xl font-semibold tracking-tight text-gray-900">
                      12,480
                    </div>
                    <div className="mt-2 text-sm font-medium text-gray-500">
                      Jobs completed through Certs.
                    </div>
                  </div>
                  <div className="metric-visual metric-grid-triangle">
                    {trianglePattern.flat().map((value, index) => (
                      <div
                        key={index}
                        className={value ? 'metric-square' : 'metric-square metric-square--empty'}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="metric-card flex flex-col justify-between rounded-3xl bg-white p-8 shadow-sm">
                <div className="flex items-center justify-between gap-6">
                  <div>
                    <div className="text-4xl font-semibold tracking-tight text-gray-900">
                      £4.7M
                    </div>
                    <div className="mt-2 text-sm font-medium text-gray-500">
                      Quotes issued by our users.
                    </div>
                  </div>
                  <div className="metric-visual metric-grid-bars">
                    {barHeights.map((height, columnIndex) => (
                      <div key={columnIndex} className="metric-column">
                        {Array.from({ length: 6 }).map((_, rowIndex) => {
                          const isActive = rowIndex >= 6 - height;
                          return (
                            <div
                              key={rowIndex}
                              className={
                                isActive
                                  ? 'metric-square'
                                  : 'metric-square metric-square--empty'
                              }
                            />
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right column: tall metric card */}
            <div className="metric-card flex flex-col justify-between rounded-3xl bg-white p-8 shadow-sm md:row-span-2">
              <div className="flex h-full flex-col justify-between gap-8">
                <div>
                  <div className="text-4xl font-semibold tracking-tight text-gray-900">
                    8,310
                  </div>
                  <div className="mt-2 text-sm font-medium text-gray-500">
                    Certificates generated and sent.
                  </div>
                </div>
                <div className="metric-visual metric-grid-stream">
                  {streamSquares.map((_, index) => (
                    <div
                      key={index}
                      className="metric-square metric-square--stream"
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Future anchors for nav links */}
        <section id="pricing" className="mx-auto mt-24 max-w-4xl text-center">
          <h3 className="mb-2 text-xl font-semibold text-gray-900">Pricing</h3>
          <p className="text-sm text-gray-600">
            Simple, transparent plans for growing trade businesses. Detailed pricing coming soon.
          </p>
        </section>

        <section id="resources" className="mx-auto mt-16 max-w-4xl text-center">
          <h3 className="mb-2 text-xl font-semibold text-gray-900">Resources</h3>
          <p className="text-sm text-gray-600">
            Guides, templates, and best practices to help you get the most from Certs.
          </p>
        </section>
      </main>
    </div>
  );
};

export default LandingPage;
